import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '@/lib/toast';
import { toFrontendKey } from '@/constants/platforms';
import { track } from '@/lib/analytics';
import { connectErrorType, oauthFailureMessage } from '@/lib/oauthResult';
import { takeOAuthRedirectResult, takeOAuthReturnRoute } from '@/lib/socialOAuthRedirect';
import { refreshUntilConnected } from '@/lib/socialConnections';

/**
 * Consumes the connect result the provider left on the URL when it redirected
 * the tab back, then puts the user back on the page they started from.
 *
 * This is the whole return path of the social-connect flow — mount it once,
 * above both the onboarding flow and the app shell, since a connect can be
 * started from either.
 *
 * A failed connect fires no webhook (PostForMe only emits
 * `social.account.created` on success), so this redirect is the ONLY failure
 * signal the frontend gets — without it a failure is a silent dead end
 * (LIL-383).
 */
export function useOAuthRedirectResult() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Captured at boot, before the router rewrote the URL. Taking it clears it,
    // so a remount can't replay the toast.
    const result = takeOAuthRedirectResult();
    if (!result) return;

    if (result.isSuccess) {
      void refreshUntilConnected(result.provider ? toFrontendKey(result.provider) : null);
    } else {
      toast.error(oauthFailureMessage(result));
      track('channel_connect_failed', {
        channel: result.provider,
        error_type: connectErrorType(result),
      });
    }

    // Back to where the connect started, which also drops the result params so
    // a refresh can't replay them. Falls back to the route the provider landed
    // us on when it redirected to an origin that never stored one.
    navigate(takeOAuthReturnRoute() ?? pathname, { replace: true });
    // `pathname` only feeds the fallback; re-runs are no-ops once the result
    // has been taken.
  }, [navigate, pathname]);
}
